import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import { WebhookDeliveryStatus, type WebhookDelivery } from '@nucleus-commerce/api-client/admin';

import { webhookEventsQueryOptions } from '../hooks/use-webhook-events';

/** Fields GET .../webhooks/{id}/deliveries sorts on; anything else is a 400. */
export const DELIVERY_SORTS = ['createdAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * status `options` drop unknown values the same way the page's filters do. Event names come from
 * the API's catalogue at runtime, so the backend drops unknown ones.
 */
export const DELIVERY_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(WebhookDeliveryStatus) },
  { key: 'event', type: 'async' },
  { key: 'createdAt', type: 'date-range' },
] as const;

export interface DeliveryFiltersOptions {
  /** The `webhooks` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
}

/** Filters of an endpoint's delivery log, in the order the filter menu lists them. */
export function deliveryFilters({
  t,
  queryClient,
  storeId,
}: DeliveryFiltersOptions): Array<FilterDef<WebhookDelivery>> {
  return [
    {
      key: 'status',
      label: t('deliveries.status'),
      type: 'enum',
      pinned: true,
      options: valuesOf(WebhookDeliveryStatus).map(({ value }) => ({
        value,
        label: t(`deliveryStatus.${value}`),
      })),
    },
    {
      key: 'event',
      label: t('deliveries.event'),
      type: 'async',
      pinned: true,
      // The catalogue is one short, cached list; the picker's text narrows it here.
      loadOptions: async (query) => {
        const events = await queryClient.query(webhookEventsQueryOptions(storeId));
        const needle = query.trim().toLowerCase();
        return events
          .filter((event) => event.includes(needle))
          .map((event) => ({ value: event, label: event }));
      },
    },
    { key: 'createdAt', label: t('deliveries.when'), type: 'date-range', aliases: ['created'] },
  ];
}
